import React, {useEffect, useMemo, useState} from 'react';
import {useAlert} from 'src/shared/hooks/useAlert';

import {Button} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import type {
  AttachmentModuleEnum,
  AttachmentRequest,
  AttachmentSimpleResponse,
} from 'src/api/ProactApiClient';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import type {IFormDocumentUploadFile} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {FormViewDate} from 'src/app/forms/FormViewDate/FormViewDate';
import {FormViewFileListRow} from 'src/app/forms/FormViewFileListRow/FormViewFileListRow';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {
  useCreateAttachmentMutation,
  useUpdateAttachmentMutation,
} from 'src/redux/queries/attachment';
import {useGetAttachmentClassificationListQuery} from 'src/redux/queries/attachmentClassification';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface AttachmentFormProps {
  mode: 'create' | 'edit' | 'view';
  entityId: number;
  attachmentType: AttachmentModuleEnum;
  data?: AttachmentSimpleResponse;
  onSaved: () => void;
  onCancelled: () => void;
}

const field: IFormFieldV2 = {
  name: 'contentUrl',
  label: 'File',
  type: 'file',
  required: true,
  validation: {
    maxLength: 10000,
  },
  fileUploadOptions: {
    maxFileSize: 150e6,
    allowMultiple: false,
    showFileSizeWarning: true,
  },
};

const buildAttachmentFormMeta = (
  classificationOptions: ISelectOption[]
): IFormFieldV2[] => [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
  },
  {
    name: 'expiresAt',
    label: 'Expiration Date',
    type: 'date',
    size: 'md',
  },
  {
    name: 'classificationId',
    label: 'Classification',
    type: 'select',
    size: 'md',
    options: classificationOptions,
  },
];

export const AttachmentForm: React.FC<AttachmentFormProps> = ({
  mode,
  entityId,
  attachmentType,
  data,
  onSaved,
  onCancelled,
}) => {
  const {setAlert} = useAlert();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contentUrl, setContentUrl] = useState<string>('');
  const [contentKey, setContentKey] = useState<string>('');
  const [createAttachment] = useCreateAttachmentMutation();
  const [updateAttachment] = useUpdateAttachmentMutation();

  const {data: classificationsData} = useGetAttachmentClassificationListQuery(
    {start: 0, length: 1000},
    {skip: mode === 'view'}
  );
  const classificationOptions = useMemo<ISelectOption[]>(
    () =>
      (classificationsData?.data ?? []).map(c => ({
        id: c.id,
        label: c.name,
      })),
    [classificationsData]
  );

  const formMeta = useMemo(
    () => buildAttachmentFormMeta(classificationOptions),
    [classificationOptions]
  );

  const initialData = useMemo(
    () =>
      toFormDataV2({
        name: data?.name ?? '',
        description: data?.description ?? '',
        expiresAt: data?.expiresAt ? data.expiresAt.toString() : '',
        classificationId: data?.classificationId
          ? String(data.classificationId)
          : '',
      }),
    [data]
  );

  const form = useProactForm({formMeta, initialData});
  const {resetForm} = form;

  useEffect(() => {
    resetForm(initialData);
  }, [initialData, resetForm]);

  useEffect(() => {
    setContentUrl(data?.contentUrl ?? '');
    setContentKey(data?.key ?? '');
  }, [data, entityId, attachmentType]);

  const handleUploadChange = (
    _: string,
    json: string,
    uploadedFiles: IFormDocumentUploadFile[]
  ): void => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      setContentUrl('');
      setContentKey('');
      return;
    }

    try {
      const file = uploadedFiles[0];
      setContentUrl(json);
      setContentKey(file.encodedKey);
    } catch {
      setContentUrl('');
      setContentKey('');
    }
  };

  const handleSave = async () => {
    const isValid = await form.validateForm();
    if (!isValid) {
      return;
    }

    try {
      setIsLoading(true);
      const values = form.methods.getValues();
      const request = {
        id: data?.id,
        attachmentType: attachmentType,
        entityId: entityId,
        name: values.name as string,
        description: values.description as string | undefined,
        expiresAt: values.expiresAt
          ? new Date(values.expiresAt as string)
          : undefined,
        key: contentKey,
        contentUrl: contentUrl,
        classificationId: values.classificationId
          ? Number(values.classificationId)
          : undefined,
      } as AttachmentRequest;

      if (mode === 'create') {
        await createAttachment(request).unwrap();
      } else {
        await updateAttachment(request).unwrap();
      }

      resetForm(
        toFormDataV2({
          name: '',
          description: '',
          expiresAt: '',
          classificationId: '',
        })
      );
      setContentUrl('');
      setContentKey('');
      onSaved();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsLoading(false);
  };

  return (
    <Box maw={760}>
      <Stack gap="md">
        {mode !== 'view' ? (
          <>
            <FormFileUploadV2
              field={field}
              value={contentUrl}
              onChange={handleUploadChange}
            />
            <ProactForm form={form}>
              <Grid gutter="md">
                <ProactField name="name" />
                <ProactField name="description" />
                <ProactField name="expiresAt" />
                <ProactField name="classificationId" />
              </Grid>
            </ProactForm>
            <Group>
              <Button onClick={handleSave} size="sm" loading={isLoading}>
                Save
              </Button>
              {mode === 'edit' && (
                <Button onClick={onCancelled} size="sm" variant="subtle">
                  Cancel
                </Button>
              )}
            </Group>
          </>
        ) : (
          <>
            {data?.name && <FormViewRow label="Name" value={data.name} />}
            {data?.description && (
              <FormViewRow label="Description" value={data.description} />
            )}
            {data?.expiresAt && (
              <FormViewDate label="Expiration Date" value={data.expiresAt} />
            )}
            {data?.classificationName && (
              <FormViewRow
                label="Classification"
                value={data.classificationName}
              />
            )}
            <FormViewFileListRow label="" value={data?.contentUrl} />
          </>
        )}
      </Stack>
    </Box>
  );
};
